import React from "react";
import styles from "./text-block.module.less";
import { graphql } from "gatsby";
import {
  ExistingThemes,
  SimplifyGatsbyQuery,
  TextBlock as TextBlockCore,
  MediaPositioningTypes,
} from "@parksandresorts/core";
import { withLivePreview } from "@parksandresorts/core";

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const LottieAnimation = React.lazy(() => import("../../block-elements/lottie-animation/lottie-animation.tsx"));

// The ContentfulAssets are messing up the automatic typing, and this is an *ugly* workaround
type RichTextRefereceTypes = SimplifyGatsbyQuery<Queries.TextBlockFragment>["richText"]["references"][number];
type NotAnAsset<T> = T extends { __typename: "ContentfulAsset" } ? never : T;
// Another, simpler, solution would be to declare the types we _do_ want to pass, put this could cause accidental omissions in the future

type Props = {
  imagePosition?: MediaPositioningTypes;
  animationPosition?: MediaPositioningTypes;
  size?: "xs" | "s" | "m" | "l";
  richText?: {
    // Second part of the workaround: Pretend we are not passing ContentfulAssets
    references: Array<NotAnAsset<RichTextRefereceTypes>>;
  };
};

const TextBlock = (props: SimplifyGatsbyQuery<Queries.TextBlockFragment> & Props) => {
  // Same as image width definition in query
  const imagePixelWidth = {
    xs: 512,
    s: 640,
    m: 960,
    l: 1440,
  };

  return (
    <TextBlockCore
      contentfulId={props.contentful_id}
      title={props.title}
      text={props.text}
      richText={props.richText}
      quoteAuthor={props.quoteAuthor}
      quoteLargeText={props.quoteLargeText}
      size={props.size ? props.size : "s"}
      image={
        props.image
          ? {
              xs: {
                description: props.image.description,
                gatsbyImageData: props.image.gatsbyImageDataXs,
              },
              s: {
                description: props.image.description,
                gatsbyImageData: props.image.gatsbyImageDataS,
              },
              m: {
                description: props.image.description,
                gatsbyImageData: props.image.gatsbyImageDataM,
              },
              l: {
                description: props.image.description,
                gatsbyImageData: props.image.gatsbyImageDataL,
              },
              cornerMobile: {
                description: props.image.description,
                gatsbyImageData: props.image.gatsbyImageDataCornerMobile,
              },
              cornerDesktop: {
                description: props.image.description,
                gatsbyImageData: props.image.gatsbyImageDataCornerDesktop,
              },
            }
          : undefined
      }
      imagePosition={props.imagePosition ?? "standard"}
      callToActionButton={props.callToActionButton}
      readMoreLabel={props.readMoreLabel}
      readLessLabel={props.readLessLabel}
      readMoreText={props.readMoreText}
      backgroundColor={props.backgroundColor}
      centerText={props.centerText}
      enableNeonHeading={props.enableNeonHeadings}
      componentTheme={props.componentTheme as ExistingThemes}
      borderPattern={props.borderPatternImage?.file.url}
      colorOverrides={
        props.useCustomColors ? { ...props.customColors, shouldUnderlineLinks: props.underlineLinks } : undefined
      }
      animationUrl={props.animation?.url}
      animationPosition={props.animationPosition ?? "standard"}
      imagePixelWidth={imagePixelWidth[props.size ?? "s"]}
    >
      {props.componentTheme === "gronan-live" && (
        <div className={styles.animationWrapper}>
          <LottieAnimation animation="iceCream" />
        </div>
      )}
    </TextBlockCore>
  );
};

export const fragmentTextBlock = graphql`
  fragment TextBlock on ContentfulTextBlock {
    ... on ContentfulTextBlock {
      contentful_id
      id
      image {
        gatsbyImageDataXs: gatsbyImageData(width: 512)
        gatsbyImageDataS: gatsbyImageData(width: 640)
        gatsbyImageDataM: gatsbyImageData(width: 960)
        gatsbyImageDataL: gatsbyImageData(width: 1440)
        gatsbyImageDataCornerMobile: gatsbyImageData(width: 100, height: 100)
        gatsbyImageDataCornerDesktop: gatsbyImageData(width: 200, height: 200, outputPixelDensities: [1])
        description
      }
      imagePosition
      animation {
        url
      }
      animationPosition
      title
      text {
        childMarkdownRemark {
          html
        }
      }
      richText {
        raw
        references {
          __typename
          ...Link
          ...ContentPageLink
          ...Asset
        }
      }
      readMoreLabel
      readLessLabel
      readMoreText {
        childMarkdownRemark {
          html
        }
      }
      quoteAuthor
      quoteLargeText
      size
      callToActionButton {
        title
        externalUrl
        internalLink {
          slug
          title
        }
        openInNewTab
      }
      centerText
      backgroundColor
      borderPatternImage {
        file {
          url
        }
      }
      enableNeonHeadings
      underlineLinks
      componentTheme
      useCustomColors
      customColors {
        backgroundColor
        textColor
        headingColor
        neonColorBase
        neonColorShine

        linkColor

        buttonTextColor
        buttonBackgroundColor
      }
    }
  }
`;

export default withLivePreview(TextBlock, {
  markdown: ["text", "readMoreText"],
  richText: ["richText"],
  file: ["borderPatternImage"],
  gatsbyImage: [
    {
      nameInContentful: "image",
      targetPaths: [
        "gatsbyImageDataXs",
        "gatsbyImageDataS",
        "gatsbyImageDataM",
        "gatsbyImageDataL",
        "gatsbyImageDataCornerMobile",
        "gatsbyImageDataCornerDesktop",
      ],
    },
  ],
});
